import sendOpenTelemetryEvent from '@storefront/utils/sendOpenTelemetryEvent';

import { KeyEvent, keyEventsByPath, keyEventsByRouteChange } from './types';
import { getAnalyticsIsAllowed, validateUserAccess } from './optanonConsent';

export const TRIGGERED_EVENTS_KEY = 'storefront-events-sent';
export const VALIDATION_STATUS_KEY = 'storefront-events-status';
export const SESSION_STATUS_KEY = 'session-status';

export async function handleAnalyticsRouteTracker(path: string) {
  if (!getAnalyticsIsAllowed() || !(await validateUserAccess())) return;
  if (!path.length) return;

  const expectedEvents = keyEventsByRouteChange[path] || [];
  const triggeredEvents = getTriggeredEvents(path);
  const isVisid = checkIfVisid(triggeredEvents);

  expectedEvents.forEach((expected) => {
    if (hasEventTriggered(expected, triggeredEvents)) {
      sendToSplunk({
        success: true,
        path: `${path}-${expected.trackingAlias}`,
        isVisid,
      });
    } else {
      sendToSplunk({
        success: false,
        path: `${path}-${expected.trackingAlias}`,
        isVisid,
      });
    }
  });
}

function getPageFlow(path: string): string[] {
  const flow = Object.keys(keyEventsByPath);
  const currentIndex = flow.indexOf(path);
  return currentIndex >= 0 ? flow.slice(0, currentIndex + 1) : [];
}

function getTriggeredEvents(path: string): KeyEvent[] {
  try {
    const raw = localStorage.getItem(TRIGGERED_EVENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed[path] || [];
  } catch {
    return [];
  }
}

function checkIfVisid(events: KeyEvent[]): boolean {
  return events.some((event) =>
    event.activity?.includes('HpOneEnrollment-v02')
  );
}

function getValidationStatus(): Record<
  string,
  { errorSent: boolean; successSent: boolean }
> {
  try {
    const statusLocalStorage = localStorage.getItem(VALIDATION_STATUS_KEY);
    return statusLocalStorage ? JSON.parse(statusLocalStorage) : {};
  } catch {
    return {};
  }
}

function saveValidationStatus(
  status: Record<string, { errorSent: boolean; successSent: boolean }>
) {
  localStorage.setItem(VALIDATION_STATUS_KEY, JSON.stringify(status));
}

export const sendToSplunk = ({
  success,
  path,
  firstAccess = false,
  isVisid = false,
}: {
  success: boolean;
  path: string;
  firstAccess?: boolean;
  isVisid?: boolean;
}) => {
  const pathName = path?.replace('/', '');
  const suffix = isVisid ? '-visid' : '';

  if (firstAccess) {
    sendOpenTelemetryEvent(
      `analytics-sd-log-access-${pathName}${suffix}`,
      JSON.stringify({ success, path }),
      '',
      !success,
      { 'analytics.success': success, 'analytics.path': path }
    );
  } else {
    const statusString = success ? 'success' : 'error';

    console.log(`analytics-sd-log-${statusString}-${pathName}${suffix}`, {
      success: success ? 1 : 0,
      pagePath: path,
    });

    sendOpenTelemetryEvent(
      `analytics-sd-log-${statusString}-${pathName}${suffix}`,
      JSON.stringify({ success, path }),
      '',
      !success,
      { 'analytics.success': success, 'analytics.path': path }
    );
  }
};

function hasEventTriggered(expected: KeyEvent, triggered: KeyEvent[]): boolean {
  return triggered.some((event) => {
    // Activity can be HpOneEnrollment-v01 or HpOneEnrollment-v02
    const activityMatch =
      event.activity === expected.activity ||
      (event.activity?.startsWith('HpOneEnrollment-v0') &&
        expected.activity?.startsWith('HpOneEnrollment-v0'));

    const baseMatch =
      activityMatch &&
      event.screenPath === expected.screenPath &&
      event.screenName === expected.screenName &&
      event.controlName === expected.controlName &&
      event.action === expected.action;

    if (!baseMatch) return false;

    // screenMode - since screen mode can be an array or not we implemented OR validation here
    if (expected.screenMode) {
      const expectedModes = Array.isArray(expected.screenMode)
        ? expected.screenMode
        : [expected.screenMode];

      const eventModes = Array.isArray(event.screenMode)
        ? event.screenMode
        : [event.screenMode];

      const modeMatch = expectedModes.some((mode) => eventModes.includes(mode));

      if (!modeMatch) return false;
    }

    // actionAuxParams - query param based validation
    if (expected.actionAuxParams) {
      const expectedParams = Array.isArray(expected.actionAuxParams)
        ? expected.actionAuxParams
        : [expected.actionAuxParams];

      const combined = expectedParams.join('&');
      const paramKeys = combined
        .split('&')
        .map((param) => param.split('=')[0].trim())
        .filter(Boolean);

      const auxParamsStr = event.actionAuxParams || '';

      const allParamsExist = paramKeys.every((key) =>
        new RegExp(`${key}=`, 'i').test(auxParamsStr as string)
      );

      if (!allParamsExist) return false;
    }

    return true;
  });
}

export const validateAnalyticsBatch = async (
  isDowntime?: boolean,
  isVisid?: boolean
) => {
  if (!getAnalyticsIsAllowed() || !validateUserAccess()) return;
  if (isDowntime) return;

  const currentPath = window.location.pathname;
  const lastStringPath = currentPath.substring(currentPath.lastIndexOf('/'));
  const flow = getPageFlow(lastStringPath);
  if (!flow.length) return;

  const status = getValidationStatus();
  let statusChanged = false;

  for (const path of flow) {
    const pageStatus = status[path] || { errorSent: false, successSent: false };
    const triggeredEvents = getTriggeredEvents(path);

    if (isFirstAccess(pageStatus)) {
      sendToSplunk({ success: false, path, firstAccess: true, isVisid });
    }

    const expectedEvents = keyEventsByPath[path] || [];
    const hasMissingEvents = hasMissingExpectedEvents(
      expectedEvents,
      triggeredEvents
    );

    if (shouldSendError(hasMissingEvents, pageStatus)) {
      markError(pageStatus, path, isVisid);
      statusChanged = true;
    }

    if (shouldSendSuccess(hasMissingEvents, pageStatus)) {
      markSuccess(pageStatus, path, isVisid);
      statusChanged = true;
    }

    status[path] = pageStatus;
  }

  if (statusChanged) {
    saveValidationStatus(status);
  }
};

function isFirstAccess(status: { errorSent: boolean; successSent: boolean }) {
  return !status.errorSent && !status.successSent;
}

function hasMissingExpectedEvents(
  expectedEvents: KeyEvent[],
  triggeredEvents: KeyEvent[]
) {
  return expectedEvents.some(
    (expected) => !hasEventTriggered(expected, triggeredEvents)
  );
}

function shouldSendError(
  hasMissingEvents: boolean,
  status: { errorSent: boolean }
) {
  return hasMissingEvents && !status.errorSent;
}

function shouldSendSuccess(
  hasMissingEvents: boolean,
  status: { successSent: boolean }
) {
  return !hasMissingEvents && !status.successSent;
}

function markError(
  pageStatus: { errorSent: boolean },
  path: string,
  isVisid: boolean
) {
  sendToSplunk({ success: false, path, isVisid });
  pageStatus.errorSent = true;
}

function markSuccess(
  pageStatus: { successSent: boolean },
  path: string,
  isVisid: boolean
) {
  sendToSplunk({ success: true, path, isVisid });
  pageStatus.successSent = true;
}
